import { FaDownload, FaFileImport, FaTimes, FaCheck } from "react-icons/fa";
import { UploadEmployeesApi } from "../../services/Api";
import React, { useState, useContext, useRef } from "react";
import AppContext from "../../AppContext";
import BtnSpinner from "../../components/BtnSpinner";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input } from 'reactstrap';
import * as XLSX from "xlsx";
import './style.css';
import { SUCCESS_RESULT } from "../../Constants";

const UPLOAD_EMPLOYEE_TEMPLATE_COLUMNS = [
  { name: "staff_code", description: "Staff ID (e.g. SOM116)" },
  { name: "staff_name", description: "Full name" },
  { name: "email", description: "Email address" },
  { name: "telephone", description: "Phone number" },
  { name: "gender", description: "Gender" },
  { name: "designation", description: "Designation / role" },
  { name: "office", description: "Office" },
  { name: "department", description: "Department" },
  { name: "joined_at", description: "Join date (YYYY-MM-DD)" },
  { name: "contract_type", description: "Contract type (e.g. Fixed Term)" },
  { name: "contract_start_date", description: "Contract start (YYYY-MM-DD)" },
  { name: "contract_end_date", description: "Contract end (YYYY-MM-DD)" },
  { name: "contracted_salary", description: "Contracted salary" },
  { name: "annual_leave_days", description: "Annual leave entitlement (days)" },
];

const downloadEmployeeUploadTemplate = () => {
  const headers = UPLOAD_EMPLOYEE_TEMPLATE_COLUMNS.map((column) => column.name);
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([headers]);
  ws["!cols"] = headers.map(() => ({ wch: 20 }));
  XLSX.utils.book_append_sheet(wb, ws, "Employees");
  XLSX.writeFile(wb, "employee_upload_template.xlsx");
};

const UploadEmployees = () => {
  const global = useContext(AppContext);
  const [file, setFile] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [uploadOption, setUploadOption] = useState("10001");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
        setErrorMsg("Please upload an Excel file (.xlsx or .xls)");
        return;
      }
      setFile(selectedFile);
      setErrorMsg("");
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setErrorMsg("Please select a file to upload");
      return;
    }

    setIsLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      const formData = new FormData();
      formData.append("Employees", file);
      formData.append("status",uploadOption );

      const response = await UploadEmployeesApi(formData, uploadOption);
      if (response.data.status == SUCCESS_RESULT) {
        setSuccessMsg(response.data.message);
        resetForm();
        return;
      } else {
        setErrorMsg(response.data.message || "An error occurred during upload");
        return;
      }

    } catch (error) {
      setErrorMsg(error.response?.data?.message || "An error occurred during upload");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOverrideConfirmation = () => {
    if (!file) {
      setErrorMsg("Please select a file to upload");
      return;
    }
    setShowConfirmation(true);
  };

  const confirm10002 = async () => {
    setIsLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      const formData = new FormData();
      formData.append("Employees", file);
            formData.append("status",uploadOption );


      const response = await UploadEmployeesApi(formData, "10002");
      if (response.data.status == SUCCESS_RESULT) {
        setSuccessMsg(response.data.message);
        resetForm();
        return;
      } else {
        setErrorMsg(response.data.message || "An error occurred during upload");
        return;
      }
    } catch (error) {
      setErrorMsg(error.response?.data?.message || "An error occurred during 10002");
    } finally {
      setIsLoading(false);
      setShowConfirmation(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileChange({ target: { files: [droppedFile] } });
    }
  };

  return (
    <div className="upload-container">
      <div className="upload-card">
        <div className="upload-header">
          <h2>Employee Data Upload</h2>
          <p className="upload-subtitle">Update your employee records by uploading an Excel file</p>
        </div>

        <div className="upload-options">
          <div className="alert alert-info mb-3" role="alert">
            <strong>Expected Excel columns</strong>
            <p className="mb-2 mt-2">
              The first row must contain these column headers exactly. Use <code>annual_leave_days</code> for leave entitlement.
            </p>
            <ul className="mb-0 pl-3">
              {UPLOAD_EMPLOYEE_TEMPLATE_COLUMNS.map((column) => (
                <li key={column.name}>
                  <code>{column.name}</code> — {column.description}
                </li>
              ))}
            </ul>
          </div>

          <FormGroup tag="fieldset">
            <legend>Choose Upload Action</legend>
            <div className="radio-group">
              <div className={`radio-option ${uploadOption === "10001" ? "active" : ""}`}>
                <input
                  type="radio"
                  id="10001"
                  name="uploadOption"
                  value="10001"
                  checked={uploadOption === "10001"}
                  onChange={() => setUploadOption("10001")}
                />
                <label htmlFor="10001">
                  <div className="radio-indicator"></div>
                  <span>Add & Update Records</span>
                  <p className="option-description">Add new employees and update existing ones</p>
                </label>
              </div>

              <div className={`radio-option ${uploadOption === "10002" ? "active" : ""}`}>
                <input
                  type="radio"
                  id="10002"
                  name="uploadOption"
                  value="10002"
                  checked={uploadOption === "10002"}
                  onChange={() => setUploadOption("10002")}
                />
                <label htmlFor="10002">
                  <div className="radio-indicator"></div>
                  <span>Override Entire Staff List</span>
                  <p className="option-description">Replace all existing records with the uploaded file</p>
                </label>
              </div>
            </div>
          </FormGroup>
        </div>

        <div
          className={`file-dropzone ${file ? "has-file" : ""}`}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileChange}
            accept=".xlsx,.xls"
            style={{ display: "none" }}
          />
          {file ? (
            <div className="file-preview">
              <div className="file-info">
                <span className="file-name">{file.name}</span>
                <span className="file-size">{(file.size / 1024).toFixed(2)} KB</span>
              </div>
              <button
                className="clear-file"
                onClick={(e) => {
                  e.stopPropagation();
                  resetForm();
                }}
              >
                <FaTimes />
              </button>
            </div>
          ) : (
            <>
              <FaFileImport className="upload-icon" />
              <p>Drag & drop your Excel file here or click to browse</p>
              <p className="file-requirements">Supports .xlsx and .xls files</p>
            </>
          )}
        </div>

        <div className="action-buttons">
          <button
            type="button"
            className="download-sample"
            onClick={downloadEmployeeUploadTemplate}
          >
            <FaDownload /> Download Sample File
          </button>

          <button
            className={`upload-button ${uploadOption === "10002" ? "override-button" : ""}`}
            onClick={uploadOption === "10002" ? handleOverrideConfirmation : handleUpload}
            disabled={isLoading}
          >
            {isLoading ? (
              <BtnSpinner
                is_loading={isLoading}
                text={uploadOption === "10002" ? "Processing..." : "Uploading..."}
                color={"white"}
                size="5"
              />
            ) : (
              <>
                <FaFileImport />
                {uploadOption === "10002" ? "Override Staff List" : "Upload Employees"}
              </>
            )}
          </button>
        </div>

        {errorMsg && (
          <div className="alert-message error">
            <FaTimes className="alert-icon" />
            <span>{errorMsg}</span>
          </div>
        )}

        {successMsg && (
          <div className="alert-message success">
            <FaCheck className="alert-icon" />
            <span>{successMsg}</span>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <Modal isOpen={showConfirmation} toggle={() => setShowConfirmation(false)} className="confirmation-modal">
        <ModalHeader toggle={() => setShowConfirmation(false)}>
          <h3>Confirm Override</h3>
        </ModalHeader>
        <ModalBody>
          <div className="warning-icon">
            <svg viewBox="0 0 24 24">
              <path d="M12 2L1 21h22L12 2zm0 3.5L19.5 19h-15L12 5.5z" />
              <path d="M12 16c.6 0 1-.4 1-1s-.4-1-1-1-1 .4-1 1 .4 1 1 1z" />
              <path d="M12 9c.6 0 1 .4 1 1v4c0 .6-.4 1-1 1s-1-.4-1-1v-4c0-.6.4-1 1-1z" />
            </svg>
          </div>
          <h4>Are you sure you want to 10002 all employee records?</h4>
          <p>This action will completely replace the existing staff list with the uploaded file. This cannot be undone.</p>
          <div className="file-confirmation">
            <strong>File to upload:</strong> {file?.name}
          </div>
        </ModalBody>
        <ModalFooter>
          <button className="cancel-button" onClick={() => setShowConfirmation(false)}>
            Cancel
          </button>
          <button className="confirm-override" onClick={confirm10002} disabled={isLoading}>
            {isLoading ? <BtnSpinner is_loading={isLoading} text="Processing..." /> : "Confirm Override"}
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default UploadEmployees;